import { configuratorBuilder } from 'jms-domain'
import { createI18n } from 'jms-i18n'

import { initConsoleLogger } from '@salsita-ai/platform-common/utils'
import { initDatadogLogger, initializeAnalyticsService } from '@salsita-ai/platform-project-sdk/react'

import { adminComponents } from './entity-components/admin/admin'
import { adminProductComponents } from './entity-components/admin/product'
import { applianceComponents } from './entity-components/appliance'
import { cabinetComponents } from './entity-components/cabinets'
import { roomComponents } from './entity-components/room'
import { wallItemComponents } from './entity-components/wall-items'
import { AppEnvironment } from './environment'
import { NotAuthenticatedPage } from './pages/NotAuthenticatedPage'
import { configuratorInterface } from './systems/configurator-interface.js'
import { presets } from './systems/presets'
import { jmsTheme } from './theme'
import { assignCountertopItemPositionTransformer } from './transformers/assign-countertop-item-position-transformer'
import { assignHoodToCookingItemTransformer } from './transformers/assign-hood-to-cooking-item'
import {
  cleanUpWallsTransformerOnRoomShapeChange,
  cleanUpWallsTransformerOnWallLengthChange,
} from './transformers/clean-up-walls-transformer'
import { onDeselectRoomTransformer } from './transformers/deselect-room-transformer'
import { fillEmptySpaceAfterWallItemDeleteTransformer } from './transformers/fill-empty-space-after-wall-item-delete'
import { fillRoomTransformer } from './transformers/fill-room-transformer'
import { fixBurnerBasePropsTransformer } from './transformers/fix-burner-base-props'
import { handleWarmingDrawerToggleTransformer } from './transformers/handle-warming-drawer-toggle'
import { initialNavigationTransformer } from './transformers/initial-navigation-transformer'
import { moveEndPanelTransformer } from './transformers/move-end-panel-transformer'
import { removeHoodWithGrillTransformer } from './transformers/remove-hood-with-grill'
import { resetStainlessSafeEnvironmentTransformer } from './transformers/reset-stainless-safe-environment-transformer'
import { ConfiguratorFooter } from './ui/ConfiguratorFooter'
import { OnLoadProvider } from './ui/on-load-provider'
import { version } from './version'

export const config = configuratorBuilder
  .withPresets(presets)
  .withComponentOptics([...cabinetComponents, ...applianceComponents, ...wallItemComponents, ...roomComponents])
  .withAdminComponentOptics([...adminComponents, ...adminProductComponents])
  .buildConfig({
    isDev: phoenix.runtimeConfig.IS_DEV,
    deploymentId: phoenix.runtimeConfig.DEPLOYMENT_ID,
    resources: {
      baseResourcesUrl: phoenix.runtimeConfig.RESOURCES_URL ?? '',
    },
    persistence: phoenix.runtimeConfig.USE_LOCAL_STORAGE_CLIENT
      ? {
          type: 'local-storage',
        }
      : {
          type: 'server',
          serverUrl: phoenix.runtimeConfig.SERVER_URL,
        },
    ui: {
      OnLoadProvider,
      preview: 'never',
      theme: jmsTheme,
      footer: <ConfiguratorFooter />,
      notAuthorizedAdminPage: <NotAuthenticatedPage />,
    },
    canvas: {
      canvasEnvironment: <AppEnvironment />,
      highlightingDisabled: true,
    },
    services: {
      logger: !phoenix.runtimeConfig.DATADOG_ENABLED
        ? initConsoleLogger()
        : initDatadogLogger({
            site: phoenix.runtimeConfig.DATADOG_SITE,
            clientToken: phoenix.runtimeConfig.DATADOG_CLIENT_TOKEN,
            service: phoenix.runtimeConfig.SERVICE_NAME,
            version,
          }),
      analyticsService: initializeAnalyticsService({
        gtmId: phoenix.runtimeConfig.GTM_ID,
        debug: phoenix.runtimeConfig.GTM_DEBUG,
        configuratorName: phoenix.runtimeConfig.SERVICE_NAME,
      }),
      i18n: createI18n(),
    },
    chat: {
      enabled: false,
    },
    features: {
      availableLocales: ['en'],

      indicatorSwitch: false,
      initialIndicatorShown: true,
      share: {
        cloneToEdit: true,
      },
    },
    createTransformers: ctx => [
      { priority: 0, transformer: initialNavigationTransformer(ctx) },
      { priority: 1, transformer: onDeselectRoomTransformer(ctx) },
      cleanUpWallsTransformerOnRoomShapeChange.register(ctx),
      cleanUpWallsTransformerOnWallLengthChange.register(ctx),
      fillRoomTransformer.register(ctx),
      resetStainlessSafeEnvironmentTransformer.register(ctx),
      fillEmptySpaceAfterWallItemDeleteTransformer.register(ctx),
      handleWarmingDrawerToggleTransformer.register(ctx),
      assignHoodToCookingItemTransformer.register(ctx),
      assignCountertopItemPositionTransformer.register(ctx),
      removeHoodWithGrillTransformer.register(ctx),
      moveEndPanelTransformer.register(ctx),
      fixBurnerBasePropsTransformer.register(ctx),
      configuratorInterface.transformerBuilder.when
        .navigated()
        .then.deselect()
        .build('Deselect on any navigation!')
        .register(ctx),
    ],
  })
